import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { updateInventory } from "src/api/inventory";
import ProductItemsField, { stockItemValidator, type StockItemsFormValues } from "src/components/inventory/ProductItemsField";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { useAuth } from "src/providers/AuthProvider";
import { getCustomizationValue, showSuccessToast } from "src/lib/utils";

const validationSchema = Yup.object().shape({
	items: Yup.array().of(stockItemValidator).min(1, "At least one item must be provided"),
});

const initialValues: StockItemsFormValues = { items: [] };

/** Restock: adds quantity to products in place, no transfer between entities. */
const UpdateInventoryModal = () => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const inventoryLabel = getCustomizationValue(user, "inventory_label", true) || "Inventory";

	const { mutate, isLoading } = useMutation(updateInventory, {
		onSuccess: () => {
			showSuccessToast(`${inventoryLabel} updated successfully`);
			queryClient.invalidateQueries(["inventory"]);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ handleSubmit }) => (
				<form onSubmit={handleSubmit} className="tw-flex tw-min-h-[360px] tw-flex-col tw-gap-4">
					<ProductItemsField emptyDescription="Search for a product above to add stock to it." />

					<Button type="submit" variant="secondary" className="tw-mt-auto tw-w-full" disabled={isLoading}>
						{isLoading ? "Updating..." : `Update ${inventoryLabel}`}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default UpdateInventoryModal;
